import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { AppDispatch, RootState } from '../../store';
import { loadProjects, saveProjectSettings } from '../../store/project/projectSlice';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { timeZoneOptions } from '../../utils/timeZone';
import { showAlert } from '../common/AppDialog';
import { getProjectMembers, setProjectMembers, updateProject, usesStoreApi } from '../../services/repo';
import type { ProjectMember, ProjectRole } from '../../services/repo';
import { isEnter } from '../../utils/keyboard';

/**
 * 프로젝트 설정 — **이름·설명**, **시간대**, **구성원**(프로젝트 권한, docs/PROJECT_ACCESS.md).
 *
 * 이름·설명은 소유자·관리자만 바꾼다(서버 store_api.update_project 가 판정). 예전에는 서버에 기능만 있고
 * 화면에 고칠 곳이 없었다 — 테라스트림 이관으로 생긴 「etl_grp (2)」 같은 이름을 바꿀 수 없었다(2026-10-12).
 *
 * 구성원은 저장 API(BE1 이 권한을 판정)를 쓸 때만 보인다. 소유자·관리자만 바꿀 수 있고, 나머지는 보기만 한다.
 *
 * 비워 두면 시스템 기본(서버 OPENFLOW_TZ, 없으면 한국시간)을 따른다. 정하면 이 프로젝트의
 *   · 워크플로 스케줄(cron)을 그 시간대로 해석하고 — 배포할 때 DAG 에 들어간다
 *   · 화면의 날짜·시각을 그 시간대로 보여 준다
 */
const ProjectSettingsDialog: React.FC<{ projectId: string; onClose: () => void }> = ({ projectId, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  useEscapeKey(onClose);
  const project = useSelector((s: RootState) => s.project.projects.find((p) => p.id === projectId));
  const systemTz = useSelector((s: RootState) => s.project.systemTimeZone);
  const saved = useSelector((s: RootState) => s.project.projectSettings[projectId]?.timeZone ?? null);
  const [tz, setTz] = useState<string>(saved || '');
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [name, setName] = useState(project?.name || '');
  const [description, setDescription] = useState(project?.description || '');
  useEffect(() => { setName(project?.name || ''); setDescription(project?.description || ''); }, [project?.name, project?.description]);
  useEffect(() => { setTz(saved || ''); }, [saved]);
  useEffect(() => { const h = window.setInterval(() => setNow(new Date()), 1000); return () => window.clearInterval(h); }, []);

  // 구성원 — null 이면 이 저장 경로에는 구성원이 없다(예전 경로)
  const [team, setTeam] = useState<{ owner: string; myRole: ProjectRole | null } | null>(null);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [loadedMembers, setLoadedMembers] = useState<ProjectMember[]>([]);
  const [newLogin, setNewLogin] = useState('');
  const [newRole, setNewRole] = useState<ProjectMember['role']>('viewer');
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!(await usesStoreApi())) return;
      try {
        const r = await getProjectMembers(projectId);
        if (!alive) return;
        setTeam({ owner: r.owner, myRole: r.myRole });
        setMembers(r.members);
        setLoadedMembers(r.members);
      } catch { /* 구성원을 못 읽어도 시간대 설정은 쓸 수 있다 */ }
    })();
    return () => { alive = false; };
  }, [projectId]);
  const canManage = team?.myRole === 'owner' || team?.myRole === 'admin';
  const membersChanged = JSON.stringify(members) !== JSON.stringify(loadedMembers);
  /** 이름·설명을 고칠 수 있는가 — 권한을 모르는 저장 경로(구성원 없음)는 서버가 판정하게 두고 열어 둔다 */
  const canRename = team ? canManage : true;
  const infoChanged = !!project && (name.trim() !== (project.name || '') || description.trim() !== (project.description || ''));
  const addMember = () => {
    const login = newLogin.trim();
    if (!login) return;
    if (login.toLowerCase() === (team?.owner || '').toLowerCase()) { showAlert(t('layout:members.isOwner')); return; }
    setMembers((cur) => [...cur.filter((m) => m.loginId.toLowerCase() !== login.toLowerCase()), { loginId: login, role: newRole }]);
    setNewLogin('');
  };

  const options = useMemo(() => timeZoneOptions(), []);
  const effective = tz || systemTz;
  /** 고른 시간대의 지금 시각 — 무엇을 고르는지 눈으로 확인하게 */
  const nowIn = (zone: string) => {
    try {
      return new Intl.DateTimeFormat('sv-SE', { timeZone: zone, dateStyle: 'short', timeStyle: 'medium' }).format(now);
    } catch { return '-'; }
  };

  const save = async () => {
    if (canRename && !name.trim()) { showAlert(t('layout:projectSettings.nameRequired')); return; }
    setBusy(true);
    try {
      if (project && canRename && infoChanged) {
        await updateProject({ ...project, name: name.trim(), description: description.trim() });
        await dispatch(loadProjects());
      }
      await dispatch(saveProjectSettings({ projectId, timeZone: tz || null })).unwrap();
      if (canManage && membersChanged) await setProjectMembers(projectId, members);
      onClose();
      if ((tz || null) !== saved) showAlert(t('layout:projectSettings.savedRedeploy'), { title: t('layout:projectSettings.title') });
    } catch (e: any) {
      showAlert(t('layout:projectSettings.saveFailed', { message: e?.message || String(e) }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-30" onMouseDown={onClose}>
      <div className="bg-white rounded shadow-lg w-[480px] max-w-[95vw]" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-800">{t('layout:projectSettings.title')}</div>
            <div className="text-xs text-gray-500 truncate">{project?.name || projectId}</div>
          </div>
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('common:ui.close')}>✕</button>
        </div>
        <div className="px-4 pt-4 space-y-2">
          <label className="block text-xs font-semibold text-gray-600" htmlFor="prj-name">{t('layout:projectSettings.name')}</label>
          <input id="prj-name" className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white disabled:bg-gray-100 disabled:text-gray-500"
            value={name} disabled={!canRename} maxLength={100} onChange={(e) => setName(e.target.value)} />
          <label className="block text-xs font-semibold text-gray-600" htmlFor="prj-desc">{t('layout:projectSettings.description')}</label>
          <textarea id="prj-desc" rows={2} className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white disabled:bg-gray-100 disabled:text-gray-500"
            value={description} disabled={!canRename} onChange={(e) => setDescription(e.target.value)} />
          {!canRename && <div className="text-[0.6875rem] text-gray-500">{t('layout:projectSettings.renameOwnerOnly')}</div>}
        </div>
        <div className="p-4 space-y-2">
          <label className="block text-xs font-semibold text-gray-600">{t('layout:projectSettings.timeZone')}</label>
          <select className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={tz} onChange={(e) => setTz(e.target.value)}>
            <option value="">{t('layout:projectSettings.systemDefault', { tz: systemTz })}</option>
            {options.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
          <div className="text-xs text-gray-600">
            {t('layout:projectSettings.nowIn', { tz: effective, time: nowIn(effective) })}
          </div>
          <div className="text-[0.6875rem] text-gray-500 leading-relaxed">{t('layout:projectSettings.hint')}</div>
        </div>
        {team && (
          <div className="px-4 pb-4 space-y-2">
            <div className="flex items-baseline justify-between">
              <label className="block text-xs font-semibold text-gray-600">{t('layout:members.title')}</label>
              {team.myRole && <span className="text-[0.6875rem] text-gray-500">{t('layout:members.mine', { role: t(`layout:members.role.${team.myRole}`) })}</span>}
            </div>
            <div className="border border-gray-200 rounded max-h-48 overflow-y-auto divide-y divide-gray-100 text-xs">
              <div className="flex items-center px-2 py-1">
                <span className="flex-1 truncate text-gray-800">{team.owner || '-'}</span>
                <span className="text-gray-500">{t('layout:members.role.owner')}</span>
              </div>
              {members.map((m) => (
                <div key={m.loginId} className="flex items-center gap-2 px-2 py-1">
                  <span className="flex-1 truncate text-gray-800">{m.loginId}</span>
                  {canManage ? (
                    <>
                      <select className="border border-gray-300 rounded pl-2 pr-7 py-0.5 text-xs bg-white" value={m.role}
                        onChange={(e) => setMembers((cur) => cur.map((x) => (x.loginId === m.loginId ? { ...x, role: e.target.value as ProjectMember['role'] } : x)))}>
                        <option value="editor">{t('layout:members.role.editor')}</option>
                        <option value="viewer">{t('layout:members.role.viewer')}</option>
                      </select>
                      <button className="px-1 text-gray-400 hover:text-red-600" title={t('layout:members.remove')}
                        onClick={() => setMembers((cur) => cur.filter((x) => x.loginId !== m.loginId))}>✕</button>
                    </>
                  ) : (
                    <span className="text-gray-500">{t(`layout:members.role.${m.role}`)}</span>
                  )}
                </div>
              ))}
              {members.length === 0 && <div className="px-2 py-1 text-gray-400">{t('layout:members.none')}</div>}
            </div>
            {canManage && (
              <div className="flex gap-2">
                <input className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs" value={newLogin} placeholder={t('layout:members.loginPlaceholder')}
                  onChange={(e) => setNewLogin(e.target.value)} onKeyDown={(e) => { if (isEnter(e)) addMember(); }} />
                <select className="border border-gray-300 rounded pl-2 pr-7 py-1 text-xs bg-white" value={newRole} onChange={(e) => setNewRole(e.target.value as ProjectMember['role'])}>
                  <option value="editor">{t('layout:members.role.editor')}</option>
                  <option value="viewer">{t('layout:members.role.viewer')}</option>
                </select>
                <button className="px-2 py-1 text-xs rounded bg-gray-200 hover:bg-gray-300 text-gray-700 disabled:opacity-50" disabled={!newLogin.trim()} onClick={addMember}>
                  {t('layout:members.add')}
                </button>
              </div>
            )}
            <div className="text-[0.6875rem] text-gray-500 leading-relaxed">{t('layout:members.hint')}</div>
          </div>
        )}
        <div className="px-4 py-2 border-t border-gray-200 flex justify-end gap-2">
          <button className="px-3 py-1 text-xs rounded bg-gray-200 hover:bg-gray-300 text-gray-700" onClick={onClose}>{t('common:ui.cancel')}</button>
          <button className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50" disabled={busy} onClick={save}>
            {t('common:ui.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectSettingsDialog;
